import { useState, useEffect, useRef, useCallback } from "react";
import { Box, Flex, Text, Button } from "@radix-ui/themes";
import { PaperPlaneIcon } from "@radix-ui/react-icons";
import Typewriter from "typewriter-effect/dist/core";
import { apiClient } from "~/services/api-client";
import { ensureMarketingToken } from "~/utils/marketing-auth";
import InsightsContent from "./insights-content";
import PricePerformanceChart from "./price-performance-chart";
import { PRESET_PORTFOLIOS } from "~/data/preset-portfolios";
import { useDelayWithProgress } from "~/hooks/use-delay-with-progress";

type TabOption = "Security Explain" | "Fund Explain" | "Portfolio Explain";

// Stock options for random selection (CAT, NVDA, NET, AVGO, AMGN)
const STOCK_OPTIONS = [
  { symbol: "CAT", figi: "BBG000BF0K17", name: "Caterpillar Inc." },
  { symbol: "NVDA", figi: "BBG000BBJQV0", name: "NVIDIA Corp." },
  { symbol: "NET", figi: "BBG001WMKHH5", name: "Cloudflare Inc." },
  { symbol: "AVGO", figi: "BBG00KHY5S69", name: "Broadcom Inc." },
  { symbol: "AMGN", figi: "BBG000BBS2Y0", name: "Amgen Inc." },
];

// Fund options for random selection (VOO, FCNTX)
const FUND_OPTIONS = [
  { symbol: "VOO", figi: "BBG0015VYNT4", name: "Vanguard S&P 500 ETF" },
  { symbol: "FCNTX", figi: "BBG000BBQQS6", name: "Fidelity Contrafund" },
];

// Portfolio options for random selection (Mag 7, Utilities)
const PORTFOLIO_OPTIONS = [
  { id: "mag7", name: "Mag 7" },
  { id: "utilities", name: "Utilities" },
];

// Format today's date as "Month Day, Year" (e.g., "January 30, 2026")
function getTodaysDate(): string {
  const today = new Date();
  return today.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// Get or set daily random selection from localStorage
function getDailyRandom<T>(key: string, options: T[]): T {
  const today = new Date().toDateString();
  const storageKey = `pebble_daily_${key}`;

  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const { date, value } = JSON.parse(stored);
      if (date === today) {
        // Find the option that matches the stored value
        const match = options.find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (opt: any) => opt.symbol === value || opt.id === value
        );
        if (match) return match;
      }
    }
  } catch (e) {
    console.error("Error reading from localStorage:", e);
  }

  // Generate new random selection for today
  const randomIndex = Math.floor(Math.random() * options.length);
  const selected = options[randomIndex];

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value = (selected as any).symbol || (selected as any).id;
    localStorage.setItem(storageKey, JSON.stringify({ date: today, value }));
  } catch (e) {
    console.error("Error writing to localStorage:", e);
  }

  return selected;
}

// Separate component for typewriter to manage its own lifecycle
function TypewriterText({
  text,
  onComplete,
}: {
  text: string;
  onComplete: () => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const typewriter = new Typewriter(ref.current, {
      delay: 30,
      cursor: "",
    });

    typewriter.typeString(text).callFunction(onComplete).start();

    return () => {
      typewriter.stop();
    };
  }, [text, onComplete]);

  return <span ref={ref}></span>;
}

interface TabContent {
  label: TabOption;
  prompt: string;
  identifier: string;
  analysisType: "security" | "fund" | "portfolio";
  displayName: string;
  portfolioId?: string;
}

// Generate tab content based on daily random selections
function generateTabContent(): TabContent[] {
  const stock = getDailyRandom("stock", STOCK_OPTIONS);
  const fund = getDailyRandom("fund", FUND_OPTIONS);
  const portfolio = getDailyRandom("portfolio", PORTFOLIO_OPTIONS);

  return [
    {
      label: "Portfolio Explain",
      prompt: `Tell me more about my ${portfolio.name} portfolio`,
      identifier: portfolio.id,
      analysisType: "portfolio",
      displayName: portfolio.name,
      portfolioId: portfolio.id,
    },
    {
      label: "Security Explain",
      prompt: `What's going on with ${stock.symbol}?`,
      identifier: stock.figi,
      analysisType: "security",
      displayName: stock.name,
    },
    {
      label: "Fund Explain",
      prompt: `How has ${fund.symbol} been performing lately?`,
      identifier: fund.figi,
      analysisType: "fund",
      displayName: fund.name,
    },
  ];
}

// Get portfolio identifiers for API
function getPortfolioIdentifiers(portfolioId: string): Record<string, number> {
  const portfolio = PRESET_PORTFOLIOS.find((p) => p.id === portfolioId);
  if (!portfolio) return {};

  return Object.fromEntries(
    portfolio.items.map((item) => [
      item.figi,
      1 / portfolio.items.length, // Equal weight for each security
    ])
  );
}

interface AnalysisResult {
  results: Record<string, string> | null;
  isLoading: boolean;
  error: string | null;
}

type InsightTabType = "positive" | "negative";

interface HeroWithTypingProps {
  autoPlay?: boolean;
}

export default function HeroWithTyping({
  autoPlay = false,
}: HeroWithTypingProps) {
  const [activeTab, setActiveTab] = useState<TabOption>("Portfolio Explain");
  const [showResults, setShowResults] = useState(false);
  const [insightTab, setInsightTab] = useState<InsightTabType>("positive");
  const [marketingToken, setMarketingToken] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult>({
    results: null,
    isLoading: false,
    error: null,
  });
  const [showSendButton, setShowSendButton] = useState(false);
  const [hasClickedAfterResults, setHasClickedAfterResults] = useState(false);
  const [showContactBubble, setShowContactBubble] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contactBubbleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Generate tab content once on mount
  const [tabContent] = useState(() => generateTabContent());

  // Random delay between 1-3 seconds (1000-3000ms) - regenerate on each send
  const [randomDelay, setRandomDelay] = useState(
    () => 1000 + Math.random() * 2000
  );

  // Use the delay with progress hook for "thinking" animation
  const { progress, isDelaying, wrapWithDelay } = useDelayWithProgress({
    minDelay: randomDelay,
    easing: "easeOut",
  });

  // Ensure we have a valid marketing token on mount
  useEffect(() => {
    // ensureMarketingToken will reuse existing token if valid, or create new one if expired/missing
    ensureMarketingToken()
      .then((token) => setMarketingToken(token))
      .catch(console.error);
  }, []);

  // Detect clicks after results are loaded
  useEffect(() => {
    if (
      showResults &&
      !isDelaying &&
      !analysis.isLoading &&
      !hasClickedAfterResults
    ) {
      const handleClick = () => {
        setHasClickedAfterResults(true);
      };

      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [showResults, isDelaying, analysis.isLoading, hasClickedAfterResults]);

  // Reset contact bubble when switching tabs
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowContactBubble(false);
    if (contactBubbleTimeoutRef.current) {
      clearTimeout(contactBubbleTimeoutRef.current);
      contactBubbleTimeoutRef.current = null;
    }
  }, [activeTab]);

  // Intersection Observer to detect when component is in view
  useEffect(() => {
    if (!autoPlay || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.5 } // Trigger when 50% visible
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [autoPlay]);

  // Auto-trigger send after typing completes in autoPlay mode
  useEffect(() => {
    if (
      autoPlay &&
      isInView &&
      showSendButton &&
      !showResults &&
      !analysis.isLoading
    ) {
      // Small delay to let user see the button before auto-sending
      const timer = setTimeout(() => {
        // Find and click the send button programmatically
        const sendButton = document.querySelector(
          'button[aria-label="Send message"], button:has(svg)'
        );
        if (sendButton instanceof HTMLButtonElement) {
          sendButton.click();
        }
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [
    autoPlay,
    isInView,
    showSendButton,
    showResults,
    analysis.isLoading,
    activeTab,
  ]);


  const currentTab = tabContent.find((tab) => tab.label === activeTab)!;

  // Get portfolio composition mad lib text
  const getPortfolioMadLib = () => {
    if (!currentTab.portfolioId) return null;
    const portfolio = PRESET_PORTFOLIOS.find(
      (p) => p.id === currentTab.portfolioId
    );
    if (!portfolio) return null;

    const symbols = portfolio.items.map((item) => item.symbol).join(", ");
    return `Your ${currentTab.displayName} portfolio includes an equal weighting of ${symbols}.`;
  };

  // Get symbol for current tab
  const getCurrentSymbol = () => {
    if (activeTab === "Portfolio Explain") return undefined;

    if (activeTab === "Security Explain") {
      const stock = tabContent.find((t) => t.label === "Security Explain");
      return STOCK_OPTIONS.find((s) => s.figi === stock?.identifier)?.symbol;
    }

    if (activeTab === "Fund Explain") {
      const fund = tabContent.find((t) => t.label === "Fund Explain");
      return FUND_OPTIONS.find((f) => f.figi === fund?.identifier)?.symbol;
    }

    return undefined;
  };

  // Stable callback for typewriter completion
  const handleTypingComplete = useCallback(() => {
    setShowSendButton(true);
  }, []);

  // Create unique key to force component remount when tab or results change
  const typewriterKey = `${activeTab}-${showResults}`;

  const handleSendMessage = async () => {
    if (analysis.isLoading || isDelaying) {
      return;
    }

    // Ensure we have a valid marketing token before making API calls
    if (!marketingToken) {
      setAnalysis({
        results: null,
        isLoading: false,
        error: "Authentication token not available",
      });
      return;
    }

    // Generate new random delay for this request (1-3 seconds)
    setRandomDelay(1000 + Math.random() * 2000);

    setShowResults(true);
    setInsightTab("positive");
    setAnalysis({ results: null, isLoading: true, error: null });

    try {
      await wrapWithDelay(async () => {
        if (
          currentTab.analysisType === "security" ||
          currentTab.analysisType === "fund"
        ) {
          const response = await apiClient.explainSecurity({
            figi: currentTab.identifier,
            rewriteStyle: "bulletize",
            timePeriod: "1M", // Always use 1 month
            providers: ["mtnews"],
            guestToken: marketingToken,
          });

          if (response.error) {
            setAnalysis({
              results: null,
              isLoading: false,
              error: response.error,
            });
          } else {
            setAnalysis({
              results: response.data?.results || null,
              isLoading: false,
              error: null,
            });
          }
        } else if (currentTab.analysisType === "portfolio") {
          // Fetch portfolio explain data
          const portfolioId = currentTab.portfolioId || currentTab.identifier;
          const portfolioIdentifiers = getPortfolioIdentifiers(portfolioId);

          const response = await apiClient.explainPortfolio({
            identifiers: portfolioIdentifiers,
            timeframe: "1M",
            rewriteStyle: "bulletize",
            macroStyle: "standard",
            sectorStyle: "bulletize",
            portfolioName: currentTab.displayName,
            providers: ["mtnews"],
            guestToken: marketingToken,
          });

          if (response.error) {
            setAnalysis({
              results: null,
              isLoading: false,
              error: response.error,
            });
          } else {
            // Extract the explanations object from the response
            const explanations = response.data?.explanations;

            if (explanations) {
              // Portfolio explain can return macro in two formats:
              // 1. Standard format: { macro: "string content" }
              // 2. Bulletize format: { macro: { positive_explanation: "...", negative_explanation: "..." } }

              let macroPositive = "";
              let macroNegative = "";

              // Check if macro is a string (standard format) or object (bulletize format)
              if (typeof explanations.macro === "string") {
                // Standard format - put all content in positive since it's not split
                macroPositive = explanations.macro;
              } else if (
                explanations.macro &&
                typeof explanations.macro === "object"
              ) {
                // Bulletize format
                macroPositive = explanations.macro.positive_explanation || "";
                macroNegative = explanations.macro.negative_explanation || "";
              }

              // Flatten format to match expected structure
              const results = {
                macro_positive: macroPositive,
                macro_negative: macroNegative,
                sector_positive:
                  typeof explanations.sector === "string"
                    ? explanations.sector
                    : explanations.sector?.positive_explanation || "",
                sector_negative:
                  typeof explanations.sector === "object"
                    ? explanations.sector?.negative_explanation || ""
                    : "",
                security_positive:
                  typeof explanations.security === "string"
                    ? explanations.security
                    : explanations.security?.positive_explanation || "",
                security_negative:
                  typeof explanations.security === "object"
                    ? explanations.security?.negative_explanation || ""
                    : "",

                // Include components data for weights and contributions
                components: response.data.components,
                figi_to_symbol_name: response.data.figi_to_symbol_name,
              };

              setAnalysis({
                results,
                isLoading: false,
                error: null,
              });
            } else {
              setAnalysis({
                results: null,
                isLoading: false,
                error: "No explanations returned",
              });
            }
          }
        }
      });
    } catch (error) {
      setAnalysis({
        results: null,
        isLoading: false,
        error: error instanceof Error ? error.message : "Analysis failed",
      });
    }
  };

  return (
    <Box
      ref={containerRef}
      className="hero-wrapper"
      style={{
        width: "100%",
      }}
    >
      {/* Header with Buttons */}
      <Box
        style={{
          padding: "16px 8px",
          marginBottom: "16px",
        }}
      >
        <Flex gap="2" wrap="wrap" justify="center">
          {tabContent.map((tab) => (
            <button
              key={tab.label}
              onClick={() => {
                setActiveTab(tab.label);
                setShowResults(false);
                setShowSendButton(false);
                setHasClickedAfterResults(false);
                setAnalysis({ results: null, isLoading: false, error: null });
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                activeTab === tab.label
                  ? "bg-[#F0F8F9] border border-[#4095A8] text-[#4095A8]"
                  : "bg-[var(--gray-3)] text-[var(--gray-12)] hover:bg-[var(--gray-4)] border-none"
              }`}
            >
              {tab.label.toUpperCase()}
            </button>
          ))}
        </Flex>
      </Box>

      {/* Content Area */}
      <Flex direction="column" align="center" gap="3">
        {/* Chat Interface with Terminal Style */}
        <Box
          style={{
            width: "100%",
            maxWidth: "1400px",
            paddingBottom: "24px",
          }}
          className="chat-container"
        >
          <Flex direction="column" gap="3">
            {/* User Message Bubble with Terminal Prompt */}
            <Box className="user-message-bubble w-full bg-white/70 backdrop-blur-xl rounded-lg px-3 py-2 pl-4 border border-white/40 shadow-lg">
              <Flex gap="3" align="center">
                <Text
                  style={{
                    color: "var(--accent-9)",
                    fontSize: "14px",
                    fontFamily: "monospace",
                  }}
                >
                  &gt;
                </Text>
                <Flex
                  align="center"
                  gap="2"
                  style={{ flex: 1, minHeight: "24px" }}
                >
                  <Text
                    style={{
                      color: "var(--gray-12)",
                      fontSize: "14px",
                      fontFamily: "monospace",
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {showResults ? (
                      currentTab.prompt
                    ) : (
                      <TypewriterText
                        key={typewriterKey}
                        text={currentTab.prompt}
                        onComplete={handleTypingComplete}
                      />
                    )}
                  </Text>
                  {!showResults && (
                    <Box
                      style={{
                        width: "32px",
                        height: "24px",
                        flexShrink: 0,
                        marginLeft: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {showSendButton && (
                        <Button
                          size="1"
                          onClick={handleSendMessage}
                          disabled={analysis.isLoading || !marketingToken}
                          style={{ cursor: "pointer" }}
                        >
                          <PaperPlaneIcon />
                        </Button>
                      )}
                    </Box>
                  )}
                </Flex>
              </Flex>
            </Box>

            {/* Assistant Response Bubble */}
            {showResults && (
              <Box className="assistant-message-bubble w-full bg-white/70 backdrop-blur-xl rounded-md p-3 border border-white/40 shadow-lg">
                <style>
                  {`
                      /* Mobile responsive styles */
                      @media (max-width: 768px) {
                        .user-message-bubble {
                          max-width: 100% !important;
                          padding: 8px 12px 8px 14px !important;
                        }

                        .assistant-message-bubble {
                          width: 100% !important;
                          padding: 10px !important;
                        }
                      }
                    `}
                </style>
                <Flex
                  direction="column"
                  gap="3"
                  style={{ paddingBottom: "24px" }}
                >
                  {/* Show loading state with progress bar while delaying */}
                  {isDelaying && (
                    <Flex
                      align="center"
                      justify="center"
                      style={{ minHeight: "200px" }}
                    >
                      <InsightsContent
                        analysisLoading={true}
                        analysisResults={null}
                        analysisError={null}
                        activeTab={insightTab}
                        onTabChange={setInsightTab}
                        name={currentTab.displayName}
                        symbol={getCurrentSymbol()}
                        subtitle={`Here are ${
                          currentTab.displayName
                        }'s insights as of ${getTodaysDate()}.`}
                        showApiCall={false}
                        newsProvider="mtnews"
                        enableCopy={false}
                        hideViewAll={true}
                        disablePerformance={true}
                        loadingProgress={progress}
                        newsTruncateWords={120}
                        newsLinkText="Read full article"
                      />
                    </Flex>
                  )}

                  {/* Show content after delay completes */}
                  {!isDelaying && !analysis.isLoading && (
                    <Box
                      onMouseEnter={() => {
                        if (
                          !showContactBubble &&
                          !contactBubbleTimeoutRef.current
                        ) {
                          contactBubbleTimeoutRef.current = setTimeout(() => {
                            setShowContactBubble(true);
                            contactBubbleTimeoutRef.current = null;
                          }, 1000);
                        }
                      }}
                      onMouseLeave={() => {
                        if (contactBubbleTimeoutRef.current) {
                          clearTimeout(contactBubbleTimeoutRef.current);
                          contactBubbleTimeoutRef.current = null;
                        }
                      }}
                    >
                      {/* Performance Chart - only for Security and Fund */}
                      {(activeTab === "Security Explain" ||
                        activeTab === "Fund Explain") &&
                        analysis.results && (
                          <Box
                            className="light light-theme chart-wrapper"
                            style={{
                              animationName: "fadeInUp",
                              animationDuration: "0.6s",
                              animationTimingFunction: "ease-out",
                              animationDelay: "0s",
                              animationFillMode: "backwards",
                            }}
                          >
                            <PricePerformanceChart
                              figi={currentTab.identifier}
                              securityInfo={{
                                symbol: getCurrentSymbol() || "",
                                name: currentTab.displayName,
                                sector: "",
                                exchange: "",
                                figi: currentTab.identifier,
                              }}
                              compact={true}
                              hideTimeframeControls={true}
                            />
                          </Box>
                        )}

                      {/* Portfolio Mad Lib */}
                      {activeTab === "Portfolio Explain" &&
                        getPortfolioMadLib() && (
                          <Box
                            style={{
                              animationName: "fadeInUp",
                              animationDuration: "0.6s",
                              animationTimingFunction: "ease-out",
                              animationFillMode: "backwards",
                            }}
                          >
                            <Flex align="center" gap="4">
                              {/* sparkles */}
                              <Box
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  flexShrink: 0,
                                }}
                              >
                                <img
                                  src="/ai_sparkles.svg"
                                  alt="ai sparkles"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                  }}
                                />
                              </Box>

                              {/* Speech Bubble */}
                              <Box
                                style={{
                                  flex: 1,
                                  backgroundColor: "var(--gray-2)",
                                  borderRadius: "8px",
                                  padding: "12px 16px",
                                  border: "1px solid var(--gray-6)",
                                  position: "relative",
                                }}
                              >
                                {/* Speech bubble tail pointing left */}
                                <Box
                                  style={{
                                    position: "absolute",
                                    left: "-8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: "0",
                                    height: "0",
                                    borderTop: "8px solid transparent",
                                    borderBottom: "8px solid transparent",
                                    borderRight: "8px solid var(--gray-6)",
                                  }}
                                />
                                <Box
                                  style={{
                                    position: "absolute",
                                    left: "-6px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: "0",
                                    height: "0",
                                    borderTop: "7px solid transparent",
                                    borderBottom: "7px solid transparent",
                                    borderRight: "7px solid var(--gray-2)",
                                  }}
                                />

                                <Text
                                  size="2"
                                  style={{
                                    color: "#090909",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {getPortfolioMadLib()}
                                </Text>
                              </Box>
                            </Flex>
                          </Box>
                        )}

                      {/* Insights Content */}
                      <Box
                        style={{
                          color: "black",
                          animationName: analysis.results ? "fadeInUp" : "none",
                          animationDuration: "0.6s",
                          animationTimingFunction: "ease-out",
                          animationDelay:
                            activeTab === "Security Explain" ||
                            activeTab === "Fund Explain"
                              ? "0.3s"
                              : "0s",
                          animationFillMode: "backwards",
                        }}
                      >
                        <InsightsContent
                          analysisLoading={false}
                          analysisResults={analysis.results}
                          analysisError={analysis.error}
                          activeTab={insightTab}
                          onTabChange={setInsightTab}
                          name={currentTab.displayName}
                          symbol={getCurrentSymbol()}
                          subtitle={`Here are ${
                            currentTab.displayName
                          }'s insights as of ${getTodaysDate()}.`}
                          showApiCall={false}
                          newsProvider="mtnews"
                          enableCopy={false}
                          hideViewAll={true}
                          disablePerformance={true}
                          newsTruncateWords={120}
                          newsLinkText="Read full article"
                        />
                      </Box>

                      {/* Disclaimer Bubbles */}
                      <Flex
                        direction="column"
                        gap="2"
                        style={{ marginTop: "20px", marginBottom: "-20px" }}
                      >
                        {/* Note Bubble - Always visible */}
                        <Box
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(236, 252, 253, 0.9) 0%, rgba(220, 245, 248, 0.95) 100%)",
                            padding: "12px 20px",
                            borderRadius: "8px",
                            border: "1px solid rgba(64, 149, 168, 0.2)",
                          }}
                        >
                          <Text
                            style={{
                              fontFamily:
                                "'IBM Plex Sans', Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                              fontWeight: "400",
                              fontSize: "13px",
                              lineHeight: "20px",
                              color: "#090909",
                              textAlign: "left",
                            }}
                          >
                            <Text
                              style={{
                                fontWeight: "600",
                                color: "#4095A8",
                              }}
                            >
                              Note:
                            </Text>{" "}
                            This is a simple demo to help illustrate Pebble
                            outputs. We are not a chatbot service or just
                            another LLM. We provide dynamic, accurate, and
                            comprehensive embedded financial intelligence that
                            can be placed almost anywhere within your experience
                            in a multitude of formats.
                          </Text>
                        </Box>

                        {/* Contact Us Bubble - Shows on mouse enter */}
                        {showContactBubble && (
                          <Box
                            style={{
                              background:
                                "linear-gradient(135deg, rgba(236, 252, 253, 0.9) 0%, rgba(220, 245, 248, 0.95) 100%)",
                              padding: "12px 20px",
                              borderRadius: "8px",
                              border: "1px solid rgba(64, 149, 168, 0.2)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "16px",
                              animationName: "fadeInUp",
                              animationDuration: "0.6s",
                              animationTimingFunction: "ease-out",
                              animationFillMode: "backwards",
                            }}
                          >
                            <Text
                              style={{
                                fontFamily:
                                  "'IBM Plex Sans', Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                                fontWeight: "400",
                                fontSize: "13px",
                                lineHeight: "20px",
                                color: "#090909",
                                textAlign: "left",
                              }}
                            >
                              Want to see more?
                            </Text>
                            <Button
                              asChild
                              size="2"
                              variant="solid"
                              style={{
                                backgroundColor: "#4095A8",
                                color: "white",
                                cursor: "pointer",
                                flexShrink: 0,
                              }}
                            >
                              <a
                                href="https://pebble.finance/#contact"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  textDecoration: "none",
                                  color: "white",
                                }}
                              >
                                Contact Us
                              </a>
                            </Button>
                          </Box>
                        )}
                      </Flex>
                    </Box>
                  )}
                </Flex>
              </Box>
            )}
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}
